/**
 * GRANULLO PLATFORM EVOLUTION — Scenario Engine Context
 * Phase 5: Scenario Library Upgrade
 * 
 * FOUNDER DIRECTIVE:
 * Scenario Library must not be a static catalog.
 * It must become a canonical simulation layer that uses real program structure and engine outputs.
 * 
 * Every scenario must be compatible with:
 * - Blueprint Graph
 * - Baseline Signals
 * - Decision Matrix
 * - Monitoring Engine
 * - Exposure Engine
 * - Cascade Engine
 * - System Stress
 * - Commit Economy
 * 
 * No mock simulation logic.
 * No decorative "what-if" cards disconnected from engines.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// SCENARIO TYPES
// ============================================================================

export const SCENARIO_TYPES = {
  DRIFT: 'drift',                           // Signal drift simulation
  AUTHORITY_DELAY: 'authority_delay',       // Authority response delay
  POLICY_CHANGE: 'policy_change',           // Policy threshold modification
  TOPOLOGY_CHANGE: 'topology_change',       // Topology density/structure changes
  INTERVENTION: 'intervention',             // Intervention comparison
  STRESS_TEST: 'stress_test',               // Combined stress test
  CASCADE: 'cascade',                       // Cascade propagation simulation
  TRANSACTION_SPIKE: 'transaction_spike',   // Volume spike simulation
  CONCENTRATION: 'concentration',           // Merchant/beneficiary concentration
  GEOGRAPHIC_IMBALANCE: 'geographic_imbalance' // Regional distribution
};

export const SCENARIO_STATUS = {
  DRAFT: 'draft',
  CONFIGURED: 'configured',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

export const TEMPLATE_CATEGORIES = {
  CBDC: 'cbdc',
  DONATION: 'donation',
  EMPLOYEE_BENEFITS: 'employee_benefits',
  CIRCULARITY: 'circularity',
  VC_PE: 'vc_pe',
  SUBSIDY: 'subsidy',
  CLIMATE_INCENTIVE: 'climate_incentive'
};

// ============================================================================
// SCENARIO OBJECT SCHEMA
// ============================================================================

/**
 * Create a canonical scenario structure
 * FOUNDER DIRECTIVE: Must support templates, live program simulations, and future System Memory ingestion
 */
export function createScenario({
  name,
  description = '',
  scenarioType,
  targetProgramId = null,
  blueprintRef = null,
  signalOverrides = {},
  policyOverrides = {},
  authorityDelayOverrides = {},
  commitPressureOverrides = {},
  interventionOptions = [],
  templateId = null,
  economicVersion = '1.0'
}) {
  return {
    // Identity
    scenario_id: `scenario_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    scenario_type: scenarioType,
    scenario_version: '1.0',
    name,
    description,
    
    // Target
    target_program_id: targetProgramId,
    blueprint_ref: blueprintRef,
    
    // Overrides (simulation inputs)
    signal_overrides: signalOverrides,       // { signalId: { value: number, thresholdMultiplier: number } }
    policy_overrides: policyOverrides,       // { policyId: { enabled: boolean, threshold: number } }
    authority_delay_overrides: authorityDelayOverrides, // { authorityLevel: delayHours }
    commit_pressure_overrides: commitPressureOverrides, // { consumedPercent, burnRate }
    
    // Intervention comparison (for intervention scenarios)
    intervention_options: interventionOptions, // Array of intervention paths to compare
    
    // Template reference
    template_id: templateId,
    template_category: null,
    
    // Economic version (pinned)
    economic_version: economicVersion,
    
    // Expected outputs (from template or manual)
    expected_outputs: {
      stressRange: null,
      exposureRange: null,
      cascadeRiskRange: null,
      decisionBacklogRange: null
    },
    
    // Actual simulation outputs (filled after run)
    actual_simulation_outputs: null,
    
    // Comparison reference (for A/B scenarios)
    comparison_ref: null,
    
    // Audit
    audit_ref: `audit_scenario_${Date.now()}`,
    
    // Status and metadata
    status: SCENARIO_STATUS.DRAFT,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    executed_at: null,
    execution_duration_ms: null
  };
}

// ============================================================================
// VERIFIED SIMULATION TEMPLATES
// ============================================================================

export const SIMULATION_TEMPLATES = {
  // CBDC Templates
  cbdc_spike: {
    id: 'cbdc_spike',
    name: 'CBDC Transaction Spike',
    category: TEMPLATE_CATEGORIES.CBDC,
    scenarioType: SCENARIO_TYPES.TRANSACTION_SPIKE,
    description: 'Simulates sudden increase in CBDC transaction volume',
    defaultOverrides: {
      signal_overrides: {
        velocity: { thresholdMultiplier: 2.0 },
        capacity: { thresholdMultiplier: 1.5 }
      },
      commit_pressure_overrides: { consumedPercent: 75, burnRate: 2.0 }
    },
    expectedPatterns: {
      stressIncrease: 'high',
      exposureType: 'capacity',
      cascadeRisk: 'moderate'
    }
  },
  
  cbdc_concentration: {
    id: 'cbdc_concentration',
    name: 'CBDC Merchant Concentration',
    category: TEMPLATE_CATEGORIES.CBDC,
    scenarioType: SCENARIO_TYPES.CONCENTRATION,
    description: 'Simulates concentration of redemptions at few merchants',
    defaultOverrides: {
      signal_overrides: {
        concentration: { value: 85, thresholdMultiplier: 1.8 }
      }
    },
    expectedPatterns: {
      stressIncrease: 'moderate',
      exposureType: 'concentration',
      cascadeRisk: 'high'
    }
  },
  
  // Donation Templates
  donation_authority_delay: {
    id: 'donation_authority_delay',
    name: 'Donation Authority Response Delay',
    category: TEMPLATE_CATEGORIES.DONATION,
    scenarioType: SCENARIO_TYPES.AUTHORITY_DELAY,
    description: 'Simulates delayed authority response in donation program',
    defaultOverrides: {
      authority_delay_overrides: {
        local_node: 48,
        policy_authority: 72,
        sponsor_authority: 120
      }
    },
    expectedPatterns: {
      stressIncrease: 'high',
      exposureType: 'authority_bottleneck',
      cascadeRisk: 'moderate'
    }
  },
  
  // Employee Benefits Templates
  benefits_policy_tightening: {
    id: 'benefits_policy_tightening',
    name: 'Benefits Policy Tightening',
    category: TEMPLATE_CATEGORIES.EMPLOYEE_BENEFITS,
    scenarioType: SCENARIO_TYPES.POLICY_CHANGE,
    description: 'Simulates stricter policy thresholds for employee benefits',
    defaultOverrides: {
      policy_overrides: {
        threshold_multiplier: 0.75 // 25% tighter
      },
      signal_overrides: {
        compliance: { thresholdMultiplier: 0.8 }
      }
    },
    expectedPatterns: {
      stressIncrease: 'moderate',
      exposureType: 'policy_misuse',
      cascadeRisk: 'low'
    }
  },
  
  // Circularity Templates
  circularity_topology_stress: {
    id: 'circularity_topology_stress',
    name: 'Circularity Network Stress',
    category: TEMPLATE_CATEGORIES.CIRCULARITY,
    scenarioType: SCENARIO_TYPES.TOPOLOGY_CHANGE,
    description: 'Simulates stress on circular economy network nodes',
    defaultOverrides: {
      signal_overrides: {
        distribution: { thresholdMultiplier: 1.5 },
        latency: { value: 150 }
      }
    },
    expectedPatterns: {
      stressIncrease: 'high',
      exposureType: 'regional_instability',
      cascadeRisk: 'high'
    }
  },
  
  // VC/PE Templates
  vcpe_cascade: {
    id: 'vcpe_cascade',
    name: 'VC/PE Cascade Failure',
    category: TEMPLATE_CATEGORIES.VC_PE,
    scenarioType: SCENARIO_TYPES.CASCADE,
    description: 'Simulates cascade effect from single fund node failure',
    defaultOverrides: {
      signal_overrides: {
        anomaly: { value: 100 }
      },
      commit_pressure_overrides: { consumedPercent: 90 }
    },
    expectedPatterns: {
      stressIncrease: 'critical',
      exposureType: 'systemic_overload',
      cascadeRisk: 'critical'
    }
  },
  
  // Climate Incentive Templates
  climate_geographic_imbalance: {
    id: 'climate_geographic_imbalance',
    name: 'Climate Credit Geographic Imbalance',
    category: TEMPLATE_CATEGORIES.CLIMATE_INCENTIVE,
    scenarioType: SCENARIO_TYPES.GEOGRAPHIC_IMBALANCE,
    description: 'Simulates geographic concentration of climate credit claims',
    defaultOverrides: {
      signal_overrides: {
        distribution: { thresholdMultiplier: 2.0 },
        concentration: { value: 70 }
      }
    },
    expectedPatterns: {
      stressIncrease: 'moderate',
      exposureType: 'regional_instability',
      cascadeRisk: 'moderate'
    }
  },
  
  // Intervention Comparison Templates
  intervention_freeze_vs_monitoring: {
    id: 'intervention_freeze_vs_monitoring',
    name: 'Freeze vs Enhanced Monitoring',
    category: TEMPLATE_CATEGORIES.CBDC,
    scenarioType: SCENARIO_TYPES.INTERVENTION,
    description: 'Compares merchant freeze intervention against enhanced monitoring',
    defaultOverrides: {
      signal_overrides: {
        concentration: { value: 80 }
      }
    },
    interventionOptions: [
      {
        id: 'freeze',
        name: 'Merchant Freeze',
        action: 'merchant_freeze',
        effects: { concentrationReduction: 50, stressIncrease: 10, commitCost: 5 }
      },
      {
        id: 'monitoring',
        name: 'Enhanced Monitoring',
        action: 'enhanced_monitoring',
        effects: { concentrationReduction: 15, stressIncrease: 5, commitCost: 2 }
      }
    ],
    expectedPatterns: {
      comparisonType: 'intervention_effectiveness'
    }
  }
};

// ============================================================================
// SIMULATION EXECUTION ENGINE
// ============================================================================

/**
 * Execute a scenario simulation
 * Uses all canonical engines to derive outputs
 */
export function executeScenario(scenario, engineInputs) {
  const {
    blueprintGraph,
    baselineSignals,
    decisionMatrix,
    commitEconomy,
    currentStress
  } = engineInputs;
  
  const startTime = Date.now();
  const outputs = {
    before: {},
    after: {},
    delta: {},
    interventionComparison: null
  };
  
  // Capture before state
  outputs.before = {
    stressIndex: currentStress?.index || 0,
    exposureScore: 0,
    cascadeRisk: 0,
    decisionBacklog: 0,
    commitPressure: 0
  };
  
  // Apply scenario overrides and calculate new state
  const simulatedSignals = applySignalOverrides(baselineSignals, scenario.signal_overrides);
  const simulatedAuthority = applyAuthorityOverrides(decisionMatrix, scenario.authority_delay_overrides);
  const simulatedCommit = applyCommitOverrides(commitEconomy, scenario.commit_pressure_overrides);
  
  // Calculate simulated outputs using engine logic
  outputs.after = calculateSimulatedOutputs({
    blueprintGraph,
    signals: simulatedSignals,
    authority: simulatedAuthority,
    commit: simulatedCommit,
    scenarioType: scenario.scenario_type
  });
  
  // Calculate deltas
  outputs.delta = {
    stressChange: outputs.after.stressIndex - outputs.before.stressIndex,
    exposureChange: outputs.after.exposureScore - outputs.before.exposureScore,
    cascadeRiskChange: outputs.after.cascadeRisk - outputs.before.cascadeRisk,
    decisionBacklogChange: outputs.after.decisionBacklog - outputs.before.decisionBacklog
  };
  
  // Run intervention comparison if applicable
  if (scenario.scenario_type === SCENARIO_TYPES.INTERVENTION && scenario.intervention_options?.length > 0) {
    outputs.interventionComparison = compareInterventions(
      scenario.intervention_options,
      outputs.before,
      engineInputs
    );
  }
  
  const executionTime = Date.now() - startTime;
  
  return {
    scenario_id: scenario.scenario_id,
    outputs,
    execution_duration_ms: executionTime,
    executed_at: new Date().toISOString(),
    engine_version: '1.0'
  };
}

function applySignalOverrides(signals, overrides) {
  if (!signals || !overrides) return signals;
  
  return signals.map(signal => {
    const override = overrides[signal.category] || overrides[signal.signal_id || signal.id];
    if (!override) return signal;
    
    return {
      ...signal,
      currentValue: override.value ?? signal.currentValue,
      thresholds: override.thresholdMultiplier ? {
        warning: signal.thresholds?.warning * override.thresholdMultiplier,
        critical: signal.thresholds?.critical * override.thresholdMultiplier,
        emergency: signal.thresholds?.emergency * override.thresholdMultiplier,
        catastrophic: signal.thresholds?.catastrophic * override.thresholdMultiplier
      } : signal.thresholds
    };
  });
}

function applyAuthorityOverrides(decisionMatrix, overrides) {
  if (!overrides) return decisionMatrix;
  return { ...decisionMatrix, authorityDelays: overrides };
}

function applyCommitOverrides(commitEconomy, overrides) {
  if (!overrides) return commitEconomy;
  return {
    ...commitEconomy,
    simulatedPressure: overrides
  };
}

function calculateSimulatedOutputs({ blueprintGraph, signals, authority, commit, scenarioType }) {
  // Simplified simulation calculation
  // In production, this would use full engine recalculation
  
  let stressIndex = 0;
  let exposureScore = 0;
  let cascadeRisk = 0;
  let decisionBacklog = 0;
  
  // Calculate based on signal states
  if (signals) {
    const driftingSignals = signals.filter(s => {
      const val = s.currentValue || 0;
      const target = s.baseline?.target || 50;
      return Math.abs(val - target) / target > 0.25;
    });
    stressIndex += driftingSignals.length * 10;
    exposureScore += driftingSignals.length * 5;
  }
  
  // Add authority delay stress
  if (authority?.authorityDelays) {
    const totalDelay = Object.values(authority.authorityDelays).reduce((a, b) => a + b, 0);
    stressIndex += Math.min(30, totalDelay / 10);
    decisionBacklog += Math.floor(totalDelay / 24);
  }
  
  // Add commit pressure
  if (commit?.simulatedPressure) {
    const pressure = commit.simulatedPressure.consumedPercent || 0;
    if (pressure > 80) stressIndex += 25;
    else if (pressure > 60) stressIndex += 15;
    else if (pressure > 40) stressIndex += 5;
  }
  
  // Add topology-based cascade risk
  if (blueprintGraph?.nodes) {
    const nodeCount = blueprintGraph.nodes.length;
    const edgeCount = blueprintGraph.edges?.length || 0;
    const density = nodeCount > 0 ? edgeCount / nodeCount : 0;
    cascadeRisk = Math.min(100, density * 20 + stressIndex * 0.3);
  }
  
  // Scenario type adjustments
  if (scenarioType === SCENARIO_TYPES.CASCADE) {
    cascadeRisk = Math.min(100, cascadeRisk * 1.5);
  } else if (scenarioType === SCENARIO_TYPES.CONCENTRATION) {
    exposureScore = Math.min(100, exposureScore * 1.3);
  }
  
  return {
    stressIndex: Math.min(100, Math.round(stressIndex)),
    exposureScore: Math.min(100, Math.round(exposureScore)),
    cascadeRisk: Math.min(100, Math.round(cascadeRisk)),
    decisionBacklog: Math.round(decisionBacklog)
  };
}

function compareInterventions(interventions, beforeState, engineInputs) {
  return interventions.map(intervention => {
    // Simulate each intervention's effect
    const effects = intervention.effects || {};
    
    const afterState = {
      stressIndex: beforeState.stressIndex + (effects.stressIncrease || 0),
      exposureScore: beforeState.exposureScore - (effects.concentrationReduction || 0) * 0.5,
      cascadeRisk: beforeState.cascadeRisk - (effects.concentrationReduction || 0) * 0.3,
      commitCost: effects.commitCost || 0
    };
    
    return {
      intervention_id: intervention.id,
      intervention_name: intervention.name,
      action: intervention.action,
      before: beforeState,
      after: afterState,
      delta: {
        stressChange: afterState.stressIndex - beforeState.stressIndex,
        exposureReduction: beforeState.exposureScore - afterState.exposureScore,
        cascadeReduction: beforeState.cascadeRisk - afterState.cascadeRisk,
        commitCost: afterState.commitCost
      },
      effectiveness_score: calculateEffectivenessScore(beforeState, afterState, intervention)
    };
  }).sort((a, b) => b.effectiveness_score - a.effectiveness_score);
}

function calculateEffectivenessScore(before, after, intervention) {
  const exposureReduction = before.exposureScore - after.exposureScore;
  const cascadeReduction = before.cascadeRisk - after.cascadeRisk;
  const stressCost = after.stressIndex - before.stressIndex;
  const commitCost = intervention.effects?.commitCost || 0;
  
  // Higher score = better intervention
  return Math.round(
    (exposureReduction * 2 + cascadeReduction * 1.5 - stressCost - commitCost * 3)
  );
}

// ============================================================================
// CONTEXT
// ============================================================================

const ScenarioEngineContext = createContext(null);

export function ScenarioEngineProvider({ children }) {
  // Scenario state
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [scenarioResults, setScenarioResults] = useState(new Map());
  const [isExecuting, setIsExecuting] = useState(false);
  
  // Engine inputs (set by integration)
  const [engineInputs, setEngineInputs] = useState({
    blueprintGraph: null,
    baselineSignals: [],
    decisionMatrix: null,
    commitEconomy: null,
    currentStress: null
  });
  
  /**
   * Update engine inputs from other contexts
   */
  const updateEngineInputs = useCallback((inputs) => {
    setEngineInputs(prev => ({ ...prev, ...inputs }));
  }, []);
  
  /**
   * Create a new scenario from template
   */
  const createFromTemplate = useCallback((templateId, customOverrides = {}) => {
    const template = SIMULATION_TEMPLATES[templateId];
    if (!template) return null;
    
    const scenario = createScenario({
      name: customOverrides.name || template.name,
      description: customOverrides.description || template.description,
      scenarioType: template.scenarioType,
      targetProgramId: customOverrides.targetProgramId,
      blueprintRef: engineInputs.blueprintGraph?.id,
      signalOverrides: { ...template.defaultOverrides?.signal_overrides, ...customOverrides.signalOverrides },
      policyOverrides: { ...template.defaultOverrides?.policy_overrides, ...customOverrides.policyOverrides },
      authorityDelayOverrides: { ...template.defaultOverrides?.authority_delay_overrides, ...customOverrides.authorityDelayOverrides },
      commitPressureOverrides: { ...template.defaultOverrides?.commit_pressure_overrides, ...customOverrides.commitPressureOverrides },
      interventionOptions: template.interventionOptions || customOverrides.interventionOptions || [],
      templateId: template.id
    });
    
    scenario.template_category = template.category;
    scenario.expected_outputs = template.expectedPatterns;
    scenario.status = SCENARIO_STATUS.CONFIGURED;
    
    setScenarios(prev => [...prev, scenario]);
    return scenario;
  }, [engineInputs.blueprintGraph]);
  
  /**
   * Create a custom scenario
   */
  const createCustomScenario = useCallback((config) => {
    const scenario = createScenario({
      ...config,
      blueprintRef: engineInputs.blueprintGraph?.id
    });
    scenario.status = SCENARIO_STATUS.CONFIGURED;
    setScenarios(prev => [...prev, scenario]);
    return scenario;
  }, [engineInputs.blueprintGraph]);
  
  /**
   * Execute a scenario
   */
  const runScenario = useCallback((scenarioId) => {
    const scenario = scenarios.find(s => s.scenario_id === scenarioId);
    if (!scenario) return null;
    
    setIsExecuting(true);
    setActiveScenario(scenario);
    
    // Update scenario status
    setScenarios(prev => prev.map(s => 
      s.scenario_id === scenarioId 
        ? { ...s, status: SCENARIO_STATUS.RUNNING }
        : s
    ));
    
    try {
      const result = executeScenario(scenario, engineInputs);
      
      // Store result
      setScenarioResults(prev => new Map(prev).set(scenarioId, result));
      
      // Update scenario with results
      setScenarios(prev => prev.map(s => 
        s.scenario_id === scenarioId 
          ? { 
              ...s, 
              status: SCENARIO_STATUS.COMPLETED,
              actual_simulation_outputs: result.outputs,
              executed_at: result.executed_at,
              execution_duration_ms: result.execution_duration_ms
            }
          : s
      ));
      
      setIsExecuting(false);
      return result;
    } catch (error) {
      setScenarios(prev => prev.map(s => 
        s.scenario_id === scenarioId 
          ? { ...s, status: SCENARIO_STATUS.FAILED }
          : s
      ));
      setIsExecuting(false);
      throw error;
    }
  }, [scenarios, engineInputs]);
  
  /**
   * Delete a scenario
   */
  const deleteScenario = useCallback((scenarioId) => {
    setScenarios(prev => prev.filter(s => s.scenario_id !== scenarioId));
    setScenarioResults(prev => {
      const newMap = new Map(prev);
      newMap.delete(scenarioId);
      return newMap;
    });
    if (activeScenario?.scenario_id === scenarioId) {
      setActiveScenario(null);
    }
  }, [activeScenario]);
  
  /**
   * Get available templates
   */
  const getTemplates = useCallback((category = null) => {
    const templates = Object.values(SIMULATION_TEMPLATES);
    if (!category) return templates;
    return templates.filter(t => t.category === category);
  }, []);
  
  /**
   * Compare two scenarios
   */
  const compareScenarios = useCallback((scenarioIdA, scenarioIdB) => {
    const resultA = scenarioResults.get(scenarioIdA);
    const resultB = scenarioResults.get(scenarioIdB);
    
    if (!resultA || !resultB) return null;
    
    return {
      scenarioA: { id: scenarioIdA, outputs: resultA.outputs },
      scenarioB: { id: scenarioIdB, outputs: resultB.outputs },
      comparison: {
        stressDelta: resultA.outputs.after.stressIndex - resultB.outputs.after.stressIndex,
        exposureDelta: resultA.outputs.after.exposureScore - resultB.outputs.after.exposureScore,
        cascadeDelta: resultA.outputs.after.cascadeRisk - resultB.outputs.after.cascadeRisk,
        betterScenario: resultA.outputs.after.stressIndex < resultB.outputs.after.stressIndex ? scenarioIdA : scenarioIdB
      }
    };
  }, [scenarioResults]);
  
  // Computed values
  const scenariosByCategory = useMemo(() => {
    const grouped = {};
    scenarios.forEach(s => {
      const cat = s.template_category || 'custom';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(s);
    });
    return grouped;
  }, [scenarios]);
  
  const completedScenarios = useMemo(() => 
    scenarios.filter(s => s.status === SCENARIO_STATUS.COMPLETED),
  [scenarios]);
  
  const value = {
    // Constants
    SCENARIO_TYPES,
    SCENARIO_STATUS,
    TEMPLATE_CATEGORIES,
    SIMULATION_TEMPLATES,
    
    // State
    scenarios,
    activeScenario,
    scenarioResults,
    isExecuting,
    engineInputs,
    
    // Computed
    scenariosByCategory,
    completedScenarios,
    
    // Actions
    updateEngineInputs,
    createFromTemplate,
    createCustomScenario,
    runScenario,
    deleteScenario,
    setActiveScenario,
    
    // Utilities
    getTemplates,
    compareScenarios,
    executeScenario,
    createScenario
  };
  
  return (
    <ScenarioEngineContext.Provider value={value}>
      {children}
    </ScenarioEngineContext.Provider>
  );
}

export function useScenarioEngine() {
  const context = useContext(ScenarioEngineContext);
  if (!context) {
    throw new Error('useScenarioEngine must be used within ScenarioEngineProvider');
  }
  return context;
}

export default ScenarioEngineContext;
